import type { FC, ReactNode } from 'react';
import { ComponentsProps, Tooltip, Typography } from '@mui/material';
import BigNumber from 'bignumber.js';

import { useFormattedBalance } from './hook';

const DATA_ZEROS = '0.00';

export type TokenFormatProps = ComponentsProps['MuiTypography'] &
  Parameters<typeof useFormattedBalance>[1] & {
    symbol?: ReactNode;
    decimals?: number;
    amount?: BigNumber | bigint | string;
    approx?: boolean;
    showAmountTip?: boolean;
    fallback?: string;
    fallbackSpace?: boolean;
    prettify?: boolean;
    wrap?: boolean;
  };

export const TokenFormat: FC<TokenFormatProps> = ({
  amount,
  decimals = 9,
  symbol,
  approx,
  maxDecimalDigits = 4,
  minDecimalDigits = 2,
  maxTotalLength = 15,
  showAmountTip = false,
  trimEllipsis,
  fallback = DATA_ZEROS,
  fallbackSpace = true,
  adaptiveDecimals,
  prettify = true,
  wrap = false,
  ...rest
}) => {
  const { actual, trimmed, isTrimmed } = useFormattedBalance(amount, {
    maxDecimalDigits,
    minDecimalDigits,
    maxTotalLength,
    trimEllipsis,
    adaptiveDecimals,
    decimals,
    prettify,
  });

  if (!amount) {
    return (
      <Typography component="span" {...rest}>
        {fallback}
        {fallbackSpace && <>&nbsp;</>}
      </Typography>
    );
  }
  const showTooltip = showAmountTip || isTrimmed;
  const bodySpace = wrap ? (
    <>
      <wbr />{' '}
    </>
  ) : (
    '\u00A0'
  );
  // we show prefix for non zero amount and if we need to show Tooltip Amount
  // overridden by explicitly set approx
  const prefix = amount && approx ? '~ ' : '';

  const body = (
    <Typography component="span" {...rest}>
      {prefix}
      {trimmed}
      {bodySpace}
      {symbol}
    </Typography>
  );

  if (showTooltip) {
    const tooltipTitle = symbol ? (
      <>
        {actual} {symbol}
      </>
    ) : (
      actual
    );

    return (
      <Tooltip
        classes={{
          tooltip: 'secondary',
          arrow: 'secondary',
        }}
        placement="top"
        title={tooltipTitle}
        arrow
      >
        {body}
      </Tooltip>
    );
  }

  return body;
};
